import {
  FetchBaseQueryError
} from "@reduxjs/toolkit/query";
import {SerializedError} from "@reduxjs/toolkit";
import { FetchArgs } from "@reduxjs/toolkit/query/react";
import { SystemError } from "@unleashed/models/SystemError";
import { t } from "i18next";
import { IetfError } from "@unleashed/models/IetfError";

export const isFetchError = (error: FetchBaseQueryError | SerializedError): error is FetchBaseQueryError =>
  Object.keys(error).includes("status");

export const isNotFound = (error: FetchBaseQueryError | SerializedError) =>
  isFetchError(error) && error.status === 404;

export const isUnauthorized = (error: FetchBaseQueryError | SerializedError) =>
  isFetchError(error) && error.status === 401;

export const isFetchArgs = (args: string | FetchArgs): args is FetchArgs =>
  Object.keys(args).includes("method");

export const isLegacyApiError = (error: unknown): error is SystemError =>
  !!error && Object.keys(error).includes("message");

export const isIetfError = (error: unknown): error is IetfError =>
  !!error && Object.keys(error).includes("type");

// AdminV2 endpoints that validate field by field (Conversion Campaigns, App
// Launcher) return [{field, message}]; the messages are written for the user.
export const isFieldErrorList = (error: unknown): error is { field?: string, message: string }[] =>
  Array.isArray(error) && error.length > 0 &&
  error.every(e => !!e && typeof e.message === "string" && e.message.length > 0);

export const GetErrorMessage = (error: FetchBaseQueryError|SerializedError, isPlainTextResponse = false) => {
  if (isFetchError(error)) {
    if(isPlainTextResponse)
      return error.data as string;
    else if(isLegacyApiError(error.data) && error.data.message)
      return error.data.message;
    else if(isFieldErrorList(error.data))
      return error.data.map(e => e.message).join(" ");
    else
      switch (error.status) {
      case 304:
        return t`Not Modified`;
      case 400:
        return t`Bad Request`;
      case 401:
        return t`Not authenticated`;
      case 403:
        return t`Not authorized to access this record`;
      case 404:
        if (isIetfError(error.data)) {
          return t`Record not found`;
        }
        return t`Route not found`;
      case 409:
        return t`You do not have the latest copy of this record`;
      case 422:
        return t`Unprocessable Entity`;
      case "FETCH_ERROR":
        return t`Unable to connect to the server`;
      default:
        if (isLegacyApiError(error.data)) {
          return t`A server error has occurred`;
        } else {
          return error.status.toString();
        }
      }
  }
  return t`An unspecified error has occurred`;
};
